import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useAuth } from '../shared/contexts/auth-context';
import { isMasterUser, PERMISSION_LABELS, USER_ROLES, ROLE_LABELS } from '../shared/utils/permissions';
import {
  GET_ALL_USERS,
  GET_AVAILABLE_PERMISSIONS,
  CREATE_USER,
  UPDATE_USER_PERMISSIONS,
  UPDATE_USER,
  DELETE_USER,
  TOGGLE_USER_STATUS,
  RESET_USER_PASSWORD
} from '../graphql/user-management';

const UserManagement = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const { data: usersData, loading: usersLoading, refetch: refetchUsers } = useQuery(GET_ALL_USERS);
  const { data: permissionsData } = useQuery(GET_AVAILABLE_PERMISSIONS);

  const [createUser] = useMutation(CREATE_USER, {
    onCompleted: () => {
      refetchUsers();
    }
  });

  const [updateUserPermissions] = useMutation(UPDATE_USER_PERMISSIONS, {
    onCompleted: () => {
      refetchUsers();
      setShowPermissionModal(false);
      setSelectedUser(null);
    }
  });

  const [deleteUser] = useMutation(DELETE_USER, {
    onCompleted: () => refetchUsers()
  });
  const [toggleUserStatus] = useMutation(TOGGLE_USER_STATUS, {
    onCompleted: () => refetchUsers()
  });
  const [resetUserPassword] = useMutation(RESET_USER_PASSWORD);

  if (!isMasterUser(user)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600">You need master user privileges to access this page.</p>
        </div>
      </div>
    );
  }

  const handleCreateUser = async (formData) => {
    try {
      await createUser({
        variables: {
          input: {
            username: formData.username,
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            password: formData.password,
            role: formData.role,
            rentalRole: formData.rentalRole || null,
            permissions: formData.permissions
          }
        }
      });
      alert('User created successfully!');
    } catch (error) {
      alert('Error creating user: ' + error.message);
    }
  };

  const handleUpdatePermissions = async (userId, permissions) => {
    try {
      await updateUserPermissions({
        variables: {
          input: { userId, permissions }
        }
      });
      alert('Permissions updated successfully!');
    } catch (error) {
      alert('Error updating permissions: ' + error.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteUser({ variables: { userId } });
        alert('User deleted successfully!');
      } catch (error) {
        alert('Error deleting user: ' + error.message);
      }
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      await toggleUserStatus({ variables: { userId } });
    } catch (error) {
      alert('Error toggling user status: ' + error.message);
    }
  };

  const handleResetPassword = async (userId) => {
    if (window.confirm('Are you sure you want to reset this user\'s password?')) {
      try {
        const { data } = await resetUserPassword({ variables: { userId } });
        alert(`Password reset successfully! New password: ${data.resetUserPassword}`);
      } catch (error) {
        alert('Error resetting password: ' + error.message);
      }
    }
  };

  const openPermissionModal = (user) => {
    setSelectedUser(user);
    setShowPermissionModal(true);
  };

  const tabs = [
    { id: 'users', label: 'Users', icon: '👥' },
    { id: 'create', label: 'Create User', icon: '➕' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage users and their permissions
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Users List */}
        {activeTab === 'users' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">All Users</h3>
            </div>
            
            {usersLoading ? (
              <div className="text-center py-4">Loading users...</div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {usersData?.getAllUsers?.map((user) => (
                  <li key={user.id} className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                            user.isActive ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                          }`}>
                            {user.firstName?.[0] || user.username[0]}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">
                              {user.firstName && user.lastName 
                                ? `${user.firstName} ${user.lastName}` 
                                : user.username}
                            </p>
                            <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              user.role === 'master' ? 'bg-purple-100 text-purple-800' :
                              user.role === 'superuser' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {ROLE_LABELS[user.role] || user.role}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">{user.email}</p>
                          <p className="text-xs text-gray-400">
                            Permissions: {user.permissions?.length || 0} assigned
                            {user.rentalRole && (
                              <span className="ml-2 inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                {user.rentalRole}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openPermissionModal(user)}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Permissions
                        </button>
                        <button
                          onClick={() => handleToggleStatus(user.id)}
                          className={`inline-flex items-center px-3 py-2 border shadow-sm text-sm font-medium rounded-md ${
                            user.isActive
                              ? 'border-red-300 text-red-700 bg-red-50 hover:bg-red-100'
                              : 'border-green-300 text-green-700 bg-green-50 hover:bg-green-100'
                          }`}
                        >
                          {user.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleResetPassword(user.id)}
                          className="inline-flex items-center px-3 py-2 border border-yellow-300 shadow-sm text-sm font-medium rounded-md text-yellow-700 bg-yellow-50 hover:bg-yellow-100"
                        >
                          Reset Password
                        </button>
                        {user.role !== 'master' && (
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Create User Form */}
        {activeTab === 'create' && (
          <CreateUserForm
            onSubmit={handleCreateUser}
            availablePermissions={permissionsData?.getAvailablePermissions || []}
          />
        )}

        {/* Permission Modal */}
        {showPermissionModal && selectedUser && (
          <PermissionModal
            user={selectedUser}
            availablePermissions={permissionsData?.getAvailablePermissions || []}
            onClose={() => {
              setShowPermissionModal(false);
              setSelectedUser(null);
            }}
            onUpdatePermissions={handleUpdatePermissions}
          />
        )}
      </div>
    </div>
  );
};

// Create User Form Component
const CreateUserForm = ({ onSubmit, availablePermissions }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    role: USER_ROLES.USER,
    rentalRole: '',
    permissions: []
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    setFormData({
      username: '',
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      role: USER_ROLES.USER,
      rentalRole: '',
      permissions: []
    });
  };

  const handlePermissionChange = (permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Create New User</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Username</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">First Name</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Last Name</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={USER_ROLES.USER}>Regular User</option>
                <option value={USER_ROLES.SUPERUSER_RENTAL}>Super User Rental</option>
                <option value={USER_ROLES.SUPERUSER_SALES}>Super User Sales</option>
                <option value={USER_ROLES.MASTER}>Master User</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Rental Role</label>
            <select
              value={formData.rentalRole}
              onChange={(e) => setFormData(prev => ({ ...prev, rentalRole: e.target.value }))}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">None</option>
              <option value="MANAGEMENT">Management</option>
              <option value="ADMIN">Admin</option>
              <option value="AGENT">Agent</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">Controls access to Rental Asset Management features</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Permissions</label>
            <div className="grid grid-cols-2 gap-3">
              {availablePermissions.map((permission) => (
                <label key={permission} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.permissions.includes(permission)}
                    onChange={() => handlePermissionChange(permission)}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {PERMISSION_LABELS[permission] || permission}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Create User
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Permission Modal Component
const PermissionModal = ({ user, availablePermissions, onClose, onUpdatePermissions }) => {
  const [selectedPermissions, setSelectedPermissions] = useState(user.permissions || []);

  const handlePermissionChange = (permission) => {
    setSelectedPermissions(prev =>
      prev.includes(permission)
        ? prev.filter(p => p !== permission)
        : [...prev, permission]
    );
  };

  const handleSave = () => {
    onUpdatePermissions(user.id, selectedPermissions);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Update Permissions for {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.username}
          </h3>
          
          <div className="space-y-3">
            {availablePermissions.map((permission) => (
              <label key={permission} className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedPermissions.includes(permission)}
                  onChange={() => handlePermissionChange(permission)}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">
                  {PERMISSION_LABELS[permission] || permission}
                </span>
              </label>
            ))}
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;